import Badge from '@cloudscape-design/components/badge'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Toggle from '@cloudscape-design/components/toggle'
import type { Profile } from '@/types'

interface Props {
  profile: Profile
  onToggle: (id: string, enabled: boolean) => void
  onEdit: (profile: Profile) => void
  onDelete: (id: string) => void
}

function extractProfileId(modelId: string): string {
  const arnMatch = modelId.match(/inference-profile\/(.+)$/)
  return arnMatch ? arnMatch[1] : modelId
}

function getCrisInfo(modelId: string, type: string): { label: string } {
  if (type === 'anthropic-direct') return { label: 'Direct API' }
  const id = extractProfileId(modelId)
  if (/^global\.\w/.test(id)) return { label: 'Global' }
  if (/^(us|eu|ap)\.\w/.test(id)) return { label: 'Cross-Region' }
  if (/application-inference-profile/.test(modelId)) return { label: 'Cross-Region (App)' }
  return { label: 'Single-Region' }
}

export function ProfileCard({ profile, onToggle, onEdit, onDelete }: Props) {
  const cris = getCrisInfo(profile.modelId, profile.type)
  return (
    <div style={{ opacity: profile.enabled ? 1 : 0.5, transition: 'opacity 0.2s' }}>
      <Container
        header={
          <Header
            actions={
              <Toggle
                checked={profile.enabled}
                onChange={({ detail }) => onToggle(profile.id, detail.checked)}
                ariaLabel={`Enable ${profile.name}`}
              >
                Enabled
              </Toggle>
            }
          >
            <SpaceBetween direction="horizontal" size="xs">
              <Badge color="blue">{profile.type}</Badge>
              <Badge color="grey">{cris.label}</Badge>
            </SpaceBetween>
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box variant="h4">{profile.name}</Box>
          <Box variant="small" color="text-body-secondary">
            <div>Model: {profile.modelId.split('/').pop()}</div>
            <div>Region: {profile.region}</div>
            {profile.guardrailId && <div>Guardrail: {profile.guardrailId}</div>}
            {profile.proxyUrl && <div>Proxy: {profile.proxyUrl}</div>}
            <div>Max tokens: {profile.maxTokens}</div>
          </Box>
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="normal" onClick={() => onEdit(profile)}>Edit</Button>
            <Button variant="normal" onClick={() => onDelete(profile.id)}>Delete</Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    </div>
  )
}
